import React, { useEffect, useRef, useState } from "react";
import { ShieldAlert, Siren, Plus, X, Phone, MapPin, Check, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

export default function Guardian() {
  const { user, setUser } = useApp();
  const [contacts, setContacts] = useState(user?.emergency_contacts || []);
  const [armed, setArmed] = useState(false);
  const [nm, setNm] = useState("");
  const [ph, setPh] = useState("");
  const [countdown, setCountdown] = useState(null); // {reason, secs}
  const [sending, setSending] = useState(false);
  const st = useRef({ avg: 0, energy: 0, window: [], impactAt: 0, fastAt: 0, timer: 0 });
  const cdRef = useRef(0);

  useEffect(() => { setContacts(user?.emergency_contacts || []); }, [user?.emergency_contacts]);

  const saveContacts = async (list) => {
    try { const { data } = await api.put("/me/emergency-contacts", { contacts: list });
      setContacts(data.emergency_contacts); if (user) setUser({ ...user, emergency_contacts: data.emergency_contacts });
      toast.success("Emergency contacts saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addContact = () => {
    if (!ph.trim()) { toast.error("Add a phone number."); return; }
    const list = [...contacts, { name: nm.trim(), phone: ph.trim() }].slice(0, 5);
    setNm(""); setPh(""); saveContacts(list);
  };
  const removeContact = (i) => saveContacts(contacts.filter((_, j) => j !== i));

  // ---- Motion watch: impact / fall / sudden stop ----
  useEffect(() => {
    if (!armed) return;
    const onMotion = (e) => {
      const a = e.accelerationIncludingGravity || e.acceleration; if (!a) return;
      const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2);
      const s = st.current; const now = Date.now();
      s.avg = s.avg ? s.avg * 0.9 + mag * 0.1 : mag;
      const hp = Math.abs(mag - s.avg);
      s.window.push(hp); if (s.window.length > 25) s.window.shift();
      s.energy = s.window.reduce((x, y) => x + y, 0) / s.window.length;
      if (s.energy > 7) s.fastAt = now;                 // was moving fast (vehicle/run)
      if (mag > 30 || hp > 22) s.impactAt = now;        // hard impact/jerk
      // sudden stop: fast motion then near-still within 1.2s
      if (s.fastAt && now - s.fastAt < 1200 && s.energy < 1.2) { s.impactAt = now; s.stop = true; }
      // after an impact, confirm stillness ~4s → likely fall/accident
      if (s.impactAt && now - s.impactAt > 3500 && now - s.impactAt < 5000 && s.energy < 1.5) {
        const reason = s.stop ? "stop" : "fall";
        s.impactAt = 0; s.stop = false;
        startCountdown(reason);
      }
    };
    window.addEventListener("devicemotion", onMotion);
    return () => window.removeEventListener("devicemotion", onMotion);
    // eslint-disable-next-line
  }, [armed]);

  const arm = async () => {
    try {
      if (typeof DeviceMotionEvent !== "undefined" && typeof DeviceMotionEvent.requestPermission === "function") {
        const p = await DeviceMotionEvent.requestPermission();
        if (p !== "granted") { toast.error("Motion permission is needed to detect falls."); return; }
      }
      setArmed(true); toast.success("Guardian is watching for falls & impacts");
    } catch { toast.error("Your device didn't allow motion access."); }
  };

  const startCountdown = (reason) => {
    if (countdown) return;
    let secs = 15;
    setCountdown({ reason, secs });
    cdRef.current = setInterval(() => {
      secs -= 1;
      if (secs <= 0) { clearInterval(cdRef.current); setCountdown(null); fireSOS(reason); }
      else setCountdown({ reason, secs });
    }, 1000);
  };
  const cancelCountdown = () => { clearInterval(cdRef.current); setCountdown(null); toast.success("Glad you're OK — alert cancelled"); };

  // Trigger from Live Vitals when a reading is abnormal (BP / heart rate / SpO₂)
  const startRef = useRef();
  startRef.current = startCountdown;
  useEffect(() => {
    const onRisk = (e) => startRef.current?.(e.detail?.reason || "vital");
    window.addEventListener("mykind-vital-risk", onRisk);
    return () => window.removeEventListener("mykind-vital-risk", onRisk);
  }, []);

  const fireSOS = async (reason = "manual") => {
    setSending(true);
    let coords = {};
    try {
      const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }));
      coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch { /* no location */ }
    try {
      const { data } = await api.post("/sos", { reason, ...coords });
      const loc = coords.lat ? " with your live location 📍" : " (location unavailable — enable location for a map link)";
      toast.success(data.notified > 0
        ? `SOS sent inside MyKind${loc} — ${data.notified} family member(s) alerted`
        : "SOS recorded — add family to your Kind Circle so they get the alert");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  // Incoming SOS alerts (this user is a family member of someone in trouble)
  const [incoming, setIncoming] = useState([]);
  useEffect(() => {
    let live = true;
    const poll = async () => {
      try { const { data } = await api.get("/sos/alerts"); if (live) setIncoming((data.alerts || []).filter((a) => !a.read)); } catch { /* */ }
    };
    poll(); const t = setInterval(poll, 20000);
    return () => { live = false; clearInterval(t); };
  }, []);
  const ackAlert = async (id) => {
    setIncoming((a) => a.filter((x) => x.id !== id));
    try { await api.post(`/sos/alerts/${id}/ack`); } catch { /* */ }
  };

  if (!user) return null;
  const REASON_TXT = { fall: "a possible fall", impact: "a hard impact", stop: "a sudden stop (possible accident)", vital: "an abnormal vital sign (possible health risk)", manual: "an SOS" };

  return (
    <div className="rud-card rounded-2xl p-4" data-testid="guardian">
      {incoming.length > 0 && (
        <div className="mb-3 space-y-2" data-testid="guardian-incoming">
          {incoming.map((a) => (
            <div key={a.id} className="rounded-xl border border-red-500/50 bg-red-500/10 p-3 flex items-center gap-3" data-testid={`sos-alert-${a.id}`}>
              <Siren size={18} className="text-red-400 animate-pulse shrink-0" />
              <div className="flex-1 min-w-0 text-sm">
                <b className="text-red-400">🚨 {a.from_name}</b> may need help — {({ fall: "possible fall", impact: "hard impact", stop: "sudden stop / accident", vital: "abnormal vital sign", manual: "SOS" })[a.reason] || "emergency"}.
                {a.maps_url && <a href={a.maps_url} target="_blank" rel="noopener noreferrer" className="text-brand underline ml-1" data-testid={`sos-map-${a.id}`}>View location</a>}
              </div>
              <button onClick={() => ackAlert(a.id)} data-testid={`sos-ack-${a.id}`} className="btn-primary rounded-full h-8 px-3 text-xs shrink-0">I'm responding</button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-full bg-red-500/15 text-red-400 grid place-items-center"><ShieldAlert size={18} /></span>
          <div>
            <div className="font-medium text-sm">Guardian — Fall & Accident Alert</div>
            <div className="text-[11px] text-sec">Detects a hard impact or sudden stop and alerts your family. <span className="text-brand">Experimental</span></div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!armed
            ? <button data-testid="guardian-arm" onClick={arm} className="btn-primary rounded-full h-9 px-4 text-sm">Turn on protection</button>
            : <button data-testid="guardian-disarm" onClick={() => setArmed(false)} className="btn-ghost rounded-full h-9 px-4 text-sm border border-emerald-500/40 text-emerald-400 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Watching</button>}
          <button data-testid="guardian-sos" onClick={() => fireSOS("manual")} disabled={sending} className="rounded-full h-9 px-4 text-sm font-semibold bg-red-500 text-white flex items-center gap-1.5 disabled:opacity-60">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Siren size={15} />} SOS
          </button>
        </div>
      </div>

      <div className="mt-3">
        <div className="text-[11px] uppercase tracking-widest text-sec mb-2">Emergency contacts (family)</div>
        {contacts.length > 0 && (
          <div className="space-y-1.5 mb-2">
            {contacts.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-sm rounded-lg border border-rud px-3 py-1.5" data-testid={`guardian-contact-${i}`}>
                <Phone size={13} className="text-brand" />
                <span className="flex-1 truncate">{c.name || "Contact"} · <span className="text-sec">{c.phone}</span></span>
                <button onClick={() => removeContact(i)} data-testid={`guardian-remove-${i}`} className="text-sec hover:text-red-400"><X size={14} /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input data-testid="guardian-name" value={nm} onChange={(e) => setNm(e.target.value)} placeholder="Name" className="rud-input rounded-lg px-3 py-2 text-sm w-28" />
          <input data-testid="guardian-phone" value={ph} onChange={(e) => setPh(e.target.value)} placeholder="Phone (with country code)" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="guardian-add" onClick={addContact} className="btn-ghost rounded-lg px-3 border border-rud text-sm flex items-center gap-1"><Plus size={14} /> Add</button>
        </div>
        <p className="text-[11px] text-sec mt-2 flex items-center gap-1"><MapPin size={11} /> On an alert we also notify your Family & Relatives circle in-app and share your live location.</p>
      </div>

      {countdown && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" data-testid="guardian-countdown">
          <div className="rud-card rounded-3xl p-8 max-w-sm w-full text-center border border-red-500/40">
            <div className="w-16 h-16 rounded-full bg-red-500/15 text-red-400 grid place-items-center mx-auto animate-pulse"><ShieldAlert size={30} /></div>
            <h3 className="font-serif text-2xl mt-4">Are you OK?</h3>
            <p className="text-sm text-sec mt-1">We detected {REASON_TXT[countdown.reason] || "an emergency"}. Sending an SOS to your family in…</p>
            <div className="font-serif text-6xl text-red-400 my-4" data-testid="guardian-count">{countdown.secs}</div>
            <div className="flex gap-2">
              <button data-testid="guardian-imok" onClick={cancelCountdown} className="btn-primary flex-1 h-12 rounded-full text-base flex items-center justify-center gap-2"><Check size={18} /> I'm OK</button>
              <button data-testid="guardian-sendnow" onClick={() => { clearInterval(cdRef.current); setCountdown(null); fireSOS(countdown.reason); }} className="rounded-full h-12 px-5 bg-red-500 text-white font-semibold flex items-center gap-2"><Siren size={18} /> Send now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
